import React, { useEffect, useState } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import AdminLayout from '../../components/Admin/AdminLayout'
import { trendDirFromPct } from './adminUi'
import { resolveIconNode, RsIcon } from '../../components/icons'

const PERIODS = ['7 Days', '30 Days', '90 Days', '1 Year']

function StatCard({ icon, cls, label, value, trend, trendDir = 'up' }) {
  return (
    <div className="admin-analytics-stat">
      <div className={`admin-analytics-stat-icon ${cls}`}>{resolveIconNode(icon, { size: 'md' })}</div>
      <div className="admin-analytics-stat-info">
        <div className="label">{label}</div>
        <div className="value">{value}</div>
        {trend && (
          <div className="sub">
            <span className={`trend-${trendDir}`}>
              <RsIcon name={trendDir === 'up' ? 'trendUp' : 'trendDown'} size="xs" />
              {' '}
              {String(trend).replace(/^\+/, '')}
            </span> vs last period
          </div>
        )}
      </div>
    </div>
  )
}

function BarChart({ data, keys, colors, legend }) {
  const max = Math.max(...data.flatMap((d) => keys.map((k) => d[k] || 0)), 1)
  return (
    <div>
      {legend && <div className="admin-chart-legend" style={{ marginBottom: 12, justifyContent: 'flex-end' }}>{legend.map((l) => <span key={l}>{l}</span>)}</div>}
      <div className="admin-chart-bars">
        {data.map((d) => (
          <div key={d.label} className="admin-bar-col">
            <div className="admin-bar-stack">
              {keys.map((k, i) => (
                <div
                  key={k}
                  className="admin-bar"
                  style={{ height: `${((d[k] || 0) / max) * 100}%`, background: colors[i] }}
                  title={`${k}: ${d[k]}`}
                />
              ))}
            </div>
            <span className="admin-bar-label">{d.label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function SimpleBars({ data, color = '#7c3aed' }) {
  const max = Math.max(...data.map((d) => d.value), 1)
  return (
    <div className="admin-chart-bars">
      {data.map((d) => (
        <div key={d.label} className="admin-bar-col">
          <div className="admin-bar single" style={{ height: `${(d.value / max) * 100}%`, background: color }} />
          <span className="admin-bar-label">{d.label}</span>
        </div>
      ))}
    </div>
  )
}

export default function AdminAnalytics() {
  const [period, setPeriod] = useState('30 Days')
  const [data, setData] = useState(null)

  useEffect(() => {
    axios.get('/api/admin/analytics-overview', { params: { period } })
      .then((r) => setData(r.data))
      .catch(() => toast.error('Failed to load analytics'))
  }, [period])

  const ov = data?.overview || {}

  return (
    <AdminLayout>
      <div className="admin-period-bar">
        <div className="admin-period-tabs">
          {PERIODS.map((p) => (
            <button
              key={p}
              type="button"
              className={`admin-period-tab${period === p ? ' active' : ''}`}
              onClick={() => setPeriod(p)}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      <div className="admin-stats-row cols-4">
        <StatCard icon="chart" cls="blue" label="Deals Created" value={ov.totalDeals ?? '—'} trend={ov.totalDealsTrend} trendDir={trendDirFromPct(ov.totalDealsTrend)} />
        <StatCard icon="dollar" cls="green" label="Capital Raised (period)" value={ov.capitalRaised ?? '—'} trend={ov.capitalTrend} trendDir={trendDirFromPct(ov.capitalTrend)} />
        <StatCard icon="users" cls="orange" label="New Registrations" value={ov.newRegistrations ?? '—'} trend={ov.registrationTrend} trendDir={trendDirFromPct(ov.registrationTrend)} />
        <StatCard icon="chat" cls="purple" label="Platform Messages" value={ov.totalMessages ?? '—'} trend={ov.messagesTrend} trendDir={trendDirFromPct(ov.messagesTrend)} />
      </div>

      <div className="admin-content-grid">
        <div className="admin-card">
          <div className="admin-card-header">
            <span className="admin-card-title">Deal Activity</span>
            <div className="admin-chart-legend"><span>New Deals</span><span>Closed</span></div>
          </div>
          <BarChart data={data?.charts?.dealActivity || []} keys={['new', 'closed']} colors={['#1d4ed8', '#16a34a']} />
        </div>
        <div className="admin-card">
          <div className="admin-card-header">
            <span className="admin-card-title">User Growth</span>
            <div className="admin-chart-legend"><span>GP</span><span>LP</span><span>Startup</span></div>
          </div>
          <BarChart data={data?.charts?.userGrowth || []} keys={['gp', 'lp', 'startup']} colors={['#1d4ed8', '#16a34a', '#e6a23c']} />
        </div>
      </div>

      <div className="admin-content-grid">
        <div className="admin-card">
          <div className="admin-card-header"><span className="admin-card-title">Fundraising Pipeline</span></div>
          <SimpleBars data={data?.charts?.pipeline || []} color="#7c3aed" />
        </div>
        <div className="admin-card">
          <div className="admin-card-header"><span className="admin-card-title">Engagement Metrics</span></div>
          <SimpleBars data={data?.charts?.engagement || []} color="#1d4ed8" />
        </div>
      </div>

      <div className="admin-content-grid">
        <div className="admin-breakdown-card">
          <div className="admin-breakdown-card-header">Deal Type Breakdown</div>
          <div className="admin-breakdown-card-body">
            {(data?.dealBreakdown || []).map((b) => (
              <div key={b.label} className="admin-breakdown-row">
                <span>{b.label}</span>
                <span><span className="bv">{b.value}</span><span className="pct">({b.pct}%)</span></span>
              </div>
            ))}
          </div>
        </div>
        <div className="admin-breakdown-card">
          <div className="admin-breakdown-card-header">Role Distribution</div>
          <div className="admin-breakdown-card-body">
            {(data?.roleBreakdown || []).map((b) => (
              <div key={b.label} className="admin-breakdown-row">
                <span>{b.label}</span>
                <span><span className="bv">{b.value}</span>{b.label !== 'Total Active Users' && <span className="pct">({b.pct}%)</span>}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="admin-activity-card">
        <div className="admin-activity-card-header">Recent Platform Activity</div>
        <div className="admin-activity-feed">
          {(data?.activity || []).map((a, i) => (
            <div key={i} className="admin-activity-item">
              <span className={`admin-activity-dot ${a.dot}`} />
              <div className="admin-activity-text" dangerouslySetInnerHTML={{ __html: a.text }} />
              <span className="admin-activity-time">{a.time}</span>
            </div>
          ))}
          {(data?.activity || []).length === 0 && (
            <p style={{ padding: 16, fontSize: 13, color: 'var(--admin-text-sec)' }}>No recent activity</p>
          )}
        </div>
      </div>
    </AdminLayout>
  )
}
